import type { LeadRow, LeadScoreFactor, LeadScoreProfile } from '@/api/types';
import { ArcMeter } from './ArcMeter';
import { formatDateTime } from './format';

/** Band → the meter's tone. `cold` is a warning, not a failure: the lead exists, it is just quiet. */
const BAND_TONE = {
  hot: 'good',
  warm: 'neutral',
  cold: 'warning',
} as const;

const BAND_LABEL = {
  hot: 'Hot',
  warm: 'Warm',
  cold: 'Cold',
} as const;

/**
 * The score, and — the entire point of this card — the arithmetic behind it.
 *
 * <b>Two scores, shown apart.</b> The left half is computed here from columns anyone can go and
 * read; the right half is what the model thought. They are never averaged: a number that blends a
 * reproducible calculation with an opinion is neither, and the disagreement between them is the
 * most informative thing on the card — a lead the rules rate 30 and the model rates 80 is exactly
 * the one a human should look at.
 *
 * <b>Factors that do not apply are drawn, not hidden.</b> A form lead has no response time; showing
 * an empty row that says so is what stops the reader assuming we answered slowly.
 */
export function LeadScoreCard({ score, lead }: { score: LeadScoreProfile; lead: LeadRow }) {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="t-heading" style={{ margin: 0 }}>Score profile</h2>
        <span className={`chip chip-band-${score.band}`}>{BAND_LABEL[score.band] ?? score.band}</span>
      </div>

      <div className="card-body">
        <div className="score-head">
          <ArcMeter
            value={score.score / 100}
            label="Rule-based score"
            display={String(score.score)}
            caption={`${score.earned}/${score.applicable_max} pts`}
            tone={BAND_TONE[score.band] ?? 'neutral'}
            size={148}
          />

          <div className="score-head-text">
            <p className="t-body" style={{ margin: 0 }}>{score.headline}</p>
            <p className="t-caption subtle" style={{ marginTop: 8, marginBottom: 0 }}>
              Computed from this lead's own columns — identical on every load, and every point below
              is traceable. Factors that cannot apply to this lead are excluded from the total rather
              than counted as zero.
            </p>
          </div>
        </div>

        <div className="score-factors">
          {score.factors.map((factor) => (
            <FactorRow key={factor.key} factor={factor} />
          ))}
        </div>

        <AiVerdict lead={lead} ruleScore={score.score} />
      </div>
    </div>
  );
}

function FactorRow({ factor }: { factor: LeadScoreFactor }) {
  const applies = factor.max_points > 0;
  const ratio = applies ? factor.points / factor.max_points : 0;

  return (
    <div className={`score-factor${applies ? '' : ' score-factor-na'}`}>
      <div className="score-factor-head">
        <span className="t-label">{factor.label}</span>
        <span className="t-caption tabular subtle">
          {applies ? `${factor.points}/${factor.max_points}` : 'n/a'}
        </span>
      </div>

      <div className="score-bar" role="img" aria-label={`${factor.label}: ${factor.points} of ${factor.max_points}`}>
        {applies && <div className="score-bar-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />}
      </div>

      <div className="t-caption subtle">{factor.detail}</div>
    </div>
  );
}

/**
 * The model's read, beside the rules but never mixed into them.
 *
 * A gap of 25 points either way is called out explicitly. That is the case worth a human's
 * attention, and nobody spots it by comparing two numbers in different corners of a page.
 */
function AiVerdict({ lead, ruleScore }: { lead: LeadRow; ruleScore: number }) {
  const ai = lead.ai_score;

  if (ai === null || ai === undefined) {
    return (
      <div className="score-ai score-ai-empty">
        <div className="t-label muted">AI verdict</div>
        <div className="t-body subtle">
          Not scored yet. Scoring runs in batches from the Intelligence view.
        </div>
      </div>
    );
  }

  const gap = ai - ruleScore;
  const divergent = Math.abs(gap) >= 25;

  return (
    <div className="score-ai">
      <div className="score-ai-head">
        <span className="t-label muted">AI verdict</span>
        <span className="score-ai-value tabular">{ai}<span className="t-caption subtle">/100</span></span>
      </div>

      {lead.ai_score_reason && <p className="t-body" style={{ margin: '4px 0 0' }}>{lead.ai_score_reason}</p>}

      <div className="t-caption subtle" style={{ marginTop: 6 }}>
        {lead.ai_scored_at ? `Scored ${formatDateTime(lead.ai_scored_at)}` : 'Scored'}
        {divergent && (
          <>
            {' · '}
            <strong>
              {gap > 0
                ? `the model rates this ${gap} points higher than the rules do`
                : `the model rates this ${Math.abs(gap)} points lower than the rules do`}
            </strong>
            {' — worth a look either way.'}
          </>
        )}
      </div>
    </div>
  );
}
